import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";

/**
 * How an administrator says opt-in was obtained.
 *
 * These mirror the methods Meta's own policy documentation lists as
 * compliant. The platform records the claim and cannot verify any of them,
 * which is why `consentNote` exists: it is where somebody says the signed
 * form is filed.
 */
export const CONSENT_METHODS = ["WEBSITE", "SMS", "PHONE", "IN_PERSON", "PAPER"] as const;

export const consentMethodSchema = z.enum(CONSENT_METHODS);
export type ConsentMethod = z.infer<typeof consentMethodSchema>;

/** Which permission a contact holds, and which list on a rule may use it. */
export const MESSAGING_PURPOSES = ["WHATSAPP", "VOICE"] as const;

export const messagingPurposeSchema = z.enum(MESSAGING_PURPOSES);
export type MessagingPurpose = z.infer<typeof messagingPurposeSchema>;

/**
 * E.164: a leading `+`, a country code that cannot start with zero, and at
 * most fifteen digits in total.
 *
 * Strict because the messaging providers are strict, and a number rejected at
 * the provider is discovered during an incident rather than while typing it.
 * Nothing here verifies the number exists or belongs to the person named.
 */
export const E164 = /^\+[1-9]\d{7,14}$/;

export const phoneSchema = z
  .string()
  // Punctuation is how humans write phone numbers, and every provider wants
  // it gone. Stripping beats telling somebody their number is wrong.
  .transform((value) => value.replace(/[\s()\-.]/g, ""))
  .pipe(z.string().regex(E164, "Use international format, for example +5493511234567."));

export const messagingContactSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),

  /**
   * Read back as a plain string rather than through `phoneSchema`: the same
   * asymmetry as elsewhere (ADR-050). A row that predates the validation has
   * to be visible so it can be corrected.
   */
  phone: z.string(),
  displayName: z.string(),
  role: z.string().nullable(),

  /**
   * WhatsApp permission. Null method and date means this number was saved
   * for calls only. A non-null `revokedAt` means WhatsApp consent was withdrawn.
   */
  consentMethod: consentMethodSchema.nullable(),
  consentedAt: z.iso.datetime().nullable(),
  consentRecordedByName: z.string().nullable(),
  consentNote: z.string().nullable(),
  revokedAt: z.iso.datetime().nullable(),

  /** Call permission. Independent of WhatsApp: agreeing to one does not agree to the other. */
  voiceConsentMethod: consentMethodSchema.nullable(),
  voiceConsentedAt: z.iso.datetime().nullable(),
  voiceConsentRecordedByName: z.string().nullable(),
  voiceConsentNote: z.string().nullable(),
  voiceRevokedAt: z.iso.datetime().nullable(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type MessagingContact = z.infer<typeof messagingContactSchema>;

/**
 * Adding a contact is asserting that consent exists.
 *
 * Every consent field is required, including the date. Defaulting
 * `consentedAt` to now would let somebody add a number without ever thinking
 * about whether permission was obtained, which is the one thing this form is
 * for.
 */
const consentNoteSchema = z.string().trim().max(500).optional();

/**
 * A contact needs at least one permission. Each block is validated only when
 * its method or date is present; a half-filled block is rejected, and a date
 * in the future is rejected because a consent record would be produced as
 * evidence.
 */
function refineConsentBlocks(
  value: {
    consentMethod?: ConsentMethod | undefined;
    consentedAt?: string | undefined;
    voiceConsentMethod?: ConsentMethod | undefined;
    voiceConsentedAt?: string | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const whatsappStarted = value.consentMethod !== undefined || value.consentedAt !== undefined;
  const voiceStarted =
    value.voiceConsentMethod !== undefined || value.voiceConsentedAt !== undefined;
  const whatsappComplete = value.consentMethod !== undefined && value.consentedAt !== undefined;
  const voiceComplete =
    value.voiceConsentMethod !== undefined && value.voiceConsentedAt !== undefined;

  if (!whatsappComplete && !voiceComplete) {
    ctx.addIssue({
      code: "custom",
      message: "Record WhatsApp consent, call consent, or both.",
      path: ["consentMethod"],
    });
  }

  if (whatsappStarted && !whatsappComplete) {
    ctx.addIssue({
      code: "custom",
      message: "WhatsApp consent needs a method and a date.",
      path: [value.consentMethod === undefined ? "consentMethod" : "consentedAt"],
    });
  }

  if (voiceStarted && !voiceComplete) {
    ctx.addIssue({
      code: "custom",
      message: "Call consent needs a method and a date.",
      path: [value.voiceConsentMethod === undefined ? "voiceConsentMethod" : "voiceConsentedAt"],
    });
  }

  if (value.consentedAt !== undefined && new Date(value.consentedAt).getTime() > Date.now()) {
    ctx.addIssue({
      code: "custom",
      message: "Consent cannot be dated in the future.",
      path: ["consentedAt"],
    });
  }

  if (
    value.voiceConsentedAt !== undefined &&
    new Date(value.voiceConsentedAt).getTime() > Date.now()
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Consent cannot be dated in the future.",
      path: ["voiceConsentedAt"],
    });
  }
}

export const createMessagingContactRequestSchema = z
  .object({
    phone: phoneSchema,
    displayName: z.string().trim().min(2).max(120),
    role: z.string().trim().max(120).optional(),
    /** WhatsApp permission. Omit the pair when this number is for calls only. */
    consentMethod: consentMethodSchema.optional(),
    consentedAt: z.iso.datetime().optional(),
    consentNote: consentNoteSchema,
    /** Call permission. Omit the pair when this number is for WhatsApp only. */
    voiceConsentMethod: consentMethodSchema.optional(),
    voiceConsentedAt: z.iso.datetime().optional(),
    voiceConsentNote: consentNoteSchema,
  })
  .extend(tenantScopeSchema.shape)
  .superRefine(refineConsentBlocks);

export type CreateMessagingContactRequest = z.infer<typeof createMessagingContactRequestSchema>;

export const revokeConsentRequestSchema = z.object({
  /** Which permission is withdrawn. Omitted means WhatsApp, so older clients keep working. */
  purpose: messagingPurposeSchema.optional(),
  reason: z.string().trim().min(1).max(500).optional(),
});

export type RevokeConsentRequest = z.infer<typeof revokeConsentRequestSchema>;

/**
 * Bringing a revoked contact back needs a fresh consent record, never a flag
 * flip. Somebody who withdrew consent and later gave it again consented on a
 * new date, by some method, and that is what has to be stored.
 */
export const reinstateConsentRequestSchema = z
  .object({
    purpose: messagingPurposeSchema.optional(),
    consentMethod: consentMethodSchema,
    consentedAt: z.iso.datetime(),
    consentNote: consentNoteSchema,
  })
  .refine((value) => new Date(value.consentedAt).getTime() <= Date.now(), {
    message: "Consent cannot be dated in the future.",
    path: ["consentedAt"],
  });

export type ReinstateConsentRequest = z.infer<typeof reinstateConsentRequestSchema>;

export const listMessagingContactsQuerySchema = listQuerySchema.extend({
  /** Defaults to the contacts that can actually be messaged. */
  includeRevoked: z.stringbool().default(false),
});

export type ListMessagingContactsQuery = z.infer<typeof listMessagingContactsQuerySchema>;

export const messagingContactListSchema = paginatedSchema(messagingContactSchema);
export type MessagingContactList = z.infer<typeof messagingContactListSchema>;
